import { themeColor } from "../../../../types/color";
import { ConciergeHomeLayoutState, LayoutPreset } from "./types";

/**
 * All child widget IDs that live on the concierge home page.
 * When a preset is applied, widgets in this list that are NOT in the preset's
 * `childWidgets` map will have their saved configs cleared (reset to component defaults).
 */
export const HOME_CHILD_WIDGET_IDS = [
  "concierge-home-greeting",
  "concierge-home-subtitle",
  "concierge-home-starters",
  "concierge-home-logo",
  "concierge-home-brand-label",
  "concierge-chat-input",
] as const;

/**
 * Full preset configuration including layout state and child widget overrides.
 */
export interface FullPresetConfig {
  layout: Partial<ConciergeHomeLayoutState>;
  /** Widget ID -> partial widget state overrides applied when the preset is selected.
   *  Widgets in HOME_CHILD_WIDGET_IDS not listed here will be reset to component defaults. */
  childWidgets?: Record<string, Record<string, any>>;
}

/**
 * Preset configurations for concierge home screen layouts.
 * Each preset maps to a specific Figma design variation.
 * `layout` controls the Home Layout widget state.
 * `childWidgets` optionally overrides child widget states (greeting, subtitle, starters, chat input, etc.).
 */
const SHARED_CHILD_WIDGETS = {
  "concierge-home-greeting": {
    greetingFontWeight: "semibold",
  },
  "concierge-home-subtitle": {
    fontWeight: "semibold",
  },
  "concierge-chat-input": {
    fontSize: 16,
    mobile_fontSize: 12,
    iconSize: 24,
    submitButtonSize: 24,
    iconGap: 12,
  },
};

const EXPANDED_CHAT_INPUT = {
  preset: "expanded",
  shadowEnabled: true,
  shadow: "8px 8px 6px 0px rgba(0, 0, 0, 0.02)",
  borderColor: themeColor("borderColor"),
  borderColorFocus: themeColor("borderColor"),
  borderRadiusSingleLine: 28,
  borderRadiusMultiLine: 28,
  borderWidth: 1,
  paddingX: 16,
  paddingY: 16,
  minHeight: 138,
  fontSize: 16,
  mobile_fontSize: 14,
  iconSize: 24,
  submitButtonSize: 24,
  iconGap: 12,
  mobile_minHeight: 116,
  mobile_paddingX: 14,
  mobile_paddingY: 14,
  mobile_borderRadiusSingleLine: 26,
  mobile_borderRadiusMultiLine: 26,
};

export const LAYOUT_PRESETS: Record<
  Exclude<LayoutPreset, "custom">,
  FullPresetConfig
> = {
  // =========================================================================
  // A-Series: Minimal / brand-focused layouts
  // =========================================================================

  /** A1 - Minimal: Logo only + search bar */
  A1: {
    layout: {
      logoPosition: "hero",
      brandLabelPosition: "hidden",
      showGreeting: false,
      showGreetingIcon: false,
      subtitlePosition: "hidden",
      startersMode: "hidden",
      maxStarters: 4,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      searchInputGap: 32,
      mobile_logoPosition: "hero",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
    },
  },

  /** A2 - Logo + Subtitle: Logo with assistant intro text */
  A2: {
    layout: {
      logoPosition: "hero",
      brandLabelPosition: "hidden",
      showGreeting: false,
      showGreetingIcon: false,
      subtitlePosition: "under-hero-logo",
      startersMode: "hidden",
      maxStarters: 4,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      searchInputGap: 32,
      mobile_logoPosition: "hero",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-subtitle": {
        fontSize: 24,
        fontWeight: "semibold",
        mobile_fontSize: 18,
      },
      "concierge-home-logo": {
        marginBottom: 32,
        mobile_marginBottom: 20,
      },
    },
  },

  /** A3 - Branded Logo: "CONCIERGE BY" label above logo */
  A3: {
    layout: {
      logoPosition: "hero",
      brandLabelPosition: "above-hero-logo",
      showGreeting: false,
      showGreetingIcon: false,
      subtitlePosition: "hidden",
      startersMode: "hidden",
      maxStarters: 4,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      searchInputGap: 48,
      mobile_logoPosition: "hero",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-brand-label": {
        fontSize: 16,
        fontWeight: "bold",
        marginBottom: 16,
        mobile_fontSize: 12,
        mobile_marginBottom: 8,
      },
    },
  },

  /** A4 - Subtitle + Pills: Subtitle text with pill starters, logo in footer */
  A4: {
    layout: {
      logoPosition: "footer",
      brandLabelPosition: "hidden",
      showGreeting: false,
      showGreetingIcon: false,
      subtitlePosition: "under-greeting",
      startersMode: "pills",
      maxStarters: 4,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      footerPaddingBottom: 0,
      searchInputGap: 24,
      mobile_logoPosition: "header",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-greeting": {
        greetingFontWeight: "semibold",
        greetingMarginBottom: 16,
      },
      "concierge-home-subtitle": {
        fontSize: 32,
        fontWeight: "semibold",
        marginBottom: 0,
      },
      "concierge-home-starters": {
        containerMarginTop: 24,
      },
      "concierge-home-logo": {
        logoWidth: 160,
        marginBottom: 64,
      },
    },
  },

  /** A5 - Greeting + Pills: Personalized greeting with pill starters */
  A5: {
    layout: {
      logoPosition: "footer",
      brandLabelPosition: "hidden",
      showGreeting: true,
      showGreetingIcon: false,
      subtitlePosition: "under-greeting",
      startersMode: "pills",
      maxStarters: 4,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      footerPaddingBottom: 0,
      searchInputGap: 24,
      mobile_logoPosition: "header",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-greeting": {
        greetingFontWeight: "semibold",
        greetingMarginBottom: 16,
        mobile_greetingMarginBottom: 8,
      },
      "concierge-home-subtitle": {
        fontSize: 32,
        fontWeight: "semibold",
        marginBottom: 0,
      },
      "concierge-home-starters": {
        containerMarginTop: 24,
      },
      "concierge-home-logo": {
        logoWidth: 160,
        marginBottom: 64,
      },
    },
  },

  /** A6 - Inline Icon Greeting + Pills: Greeting with inline icon before text and pill starters */
  A6: {
    layout: {
      logoPosition: "footer",
      brandLabelPosition: "hidden",
      showGreeting: true,
      showGreetingIcon: true,
      greetingIconPosition: "inline",
      subtitlePosition: "under-greeting",
      startersMode: "pills",
      maxStarters: 4,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      footerPaddingBottom: 0,
      searchInputGap: 24,
      mobile_logoPosition: "header",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-greeting": {
        greetingFontWeight: "semibold",
        greetingMarginBottom: 16,
        mobile_greetingMarginBottom: 8,
      },
      "concierge-home-subtitle": {
        fontSize: 32,
        fontWeight: "semibold",
        marginBottom: 0,
      },
      "concierge-home-starters": {
        containerMarginTop: 24,
      },
      "concierge-home-logo": {
        logoWidth: 160,
        marginBottom: 64,
      },
      "concierge-chat-input": {
        preset: "expanded",
        shadowEnabled: true,
        shadow: "8px 8px 6px 0px rgba(0, 0, 0, 0.02)",
        borderColor: themeColor("borderColor"),
        borderColorFocus: themeColor("borderColor"),
        borderRadiusSingleLine: 28,
        borderRadiusMultiLine: 28,
        borderWidth: 1,
        paddingX: 16,
        paddingY: 16,
        minHeight: 138,
        fontSize: 16,
        mobile_fontSize: 14,
        iconSize: 24,
        submitButtonSize: 24,
        iconGap: 12,
        mobile_minHeight: 116,
        mobile_paddingX: 14,
        mobile_paddingY: 14,
        mobile_borderRadiusSingleLine: 26,
        mobile_borderRadiusMultiLine: 26,
      },
    },
  },

  /** A7 - Icon Above Greeting + Pills: Greeting with icon above text and pill starters */
  A7: {
    layout: {
      logoPosition: "footer",
      brandLabelPosition: "hidden",
      showGreeting: true,
      showGreetingIcon: true,
      greetingIconPosition: "above",
      subtitlePosition: "under-greeting",
      startersMode: "pills",
      maxStarters: 4,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      footerPaddingBottom: 0,
      searchInputGap: 24,
      mobile_logoPosition: "header",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      "concierge-home-greeting": {
        greetingFontWeight: "semibold",
        greetingMarginBottom: 16,
      },
      "concierge-home-subtitle": {
        fontSize: 32,
        fontWeight: "semibold",
        lineHeight: 1.2,
        letterSpacing: 0.08,
        maxWidth: 830,
        marginBottom: 0,
      },
      "concierge-home-starters": {
        pillPaddingX: 24,
        pillPaddingY: 12,
        pillBgColor: themeColor("secondary"),
        pillFontWeight: "medium",
        pillShadowEnabled: true,
        pillShadow: "0 4px 16px 0 rgba(0, 0, 0, 0.05)",
      },
      "concierge-home-logo": {
        logoWidth: 160,
        marginBottom: 64,
      },
      "concierge-chat-input": {
        shadowEnabled: true,
        shadow: "8px 8px 6px 0px rgba(0, 0, 0, 0.02)",
        borderColor: themeColor("borderColor"),
        borderColorFocus: themeColor("borderColor"),
        borderRadiusSingleLine: 28,
        borderRadiusMultiLine: 28,
        submitButtonIconColor: themeColor("background"),
        submitButtonSize: 24,
        iconSize: 24,
        iconGap: 12,
        fontSize: 16,
        mobile_fontSize: 14,
        paddingX: 16,
        paddingY: 16,
        minHeight: 138,
        mobile_minHeight: 116,
        mobile_paddingX: 14,
        mobile_paddingY: 14,
        mobile_borderRadiusSingleLine: 26,
        mobile_borderRadiusMultiLine: 26,
      },
    },
  },

  // =========================================================================
  // B-Series: Greeting + conversation starters focused
  // =========================================================================

  /** B1 - Card Starters Row: Greeting with 4 cards in a horizontal row */
  B1: {
    layout: {
      logoPosition: "footer",
      brandLabelPosition: "hidden",
      showGreeting: true,
      showGreetingIcon: false,
      subtitlePosition: "under-greeting",
      startersMode: "cards-row",
      maxStarters: 4,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      footerPaddingBottom: 0,
      searchInputGap: 32,
      mobile_logoPosition: "header",
      mobile_startersMode: "cards-grid",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-greeting": { greetingMarginBottom: 16 },
      "concierge-home-logo": { marginBottom: 64 },
      "concierge-chat-input": EXPANDED_CHAT_INPUT,
    },
  },

  /** B2 - Icon Pills: Greeting with 3 icon-pill starters, logo after content */
  B2: {
    layout: {
      logoPosition: "after-content",
      brandLabelPosition: "hidden",
      showGreeting: true,
      showGreetingIcon: false,
      subtitlePosition: "under-greeting",
      startersMode: "pills-icons",
      maxStarters: 3,
      cardLayoutDirection: "stacked",
      footerGap: 56,
      searchInputGap: 32,
      mobile_logoPosition: "header",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-greeting": { greetingMarginBottom: 16 },
      "concierge-chat-input": EXPANDED_CHAT_INPUT,
    },
  },

  /** B3 - Icon Pills + Branded Footer: 3 icon-pill starters with "CONCIERGE BY" footer */
  B3: {
    layout: {
      logoPosition: "footer",
      brandLabelPosition: "above-footer-logo",
      showGreeting: true,
      showGreetingIcon: false,
      subtitlePosition: "under-greeting",
      startersMode: "pills-icons",
      maxStarters: 3,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      footerPaddingBottom: 0,
      searchInputGap: 32,
      mobile_logoPosition: "header",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-greeting": { greetingMarginBottom: 16 },
      "concierge-home-logo": { marginBottom: 64 },
      "concierge-chat-input": EXPANDED_CHAT_INPUT,
    },
  },

  /** B4 - Card Grid: Greeting with 2x2 inline-icon card grid */
  B4: {
    layout: {
      logoPosition: "footer",
      brandLabelPosition: "hidden",
      showGreeting: true,
      showGreetingIcon: false,
      subtitlePosition: "under-greeting",
      startersMode: "cards-grid",
      maxStarters: 4,
      cardLayoutDirection: "inline",
      footerGap: 8,
      footerPaddingBottom: 0,
      searchInputGap: 32,
      mobile_logoPosition: "header",
      mobile_startersMode: "pills-icons-list",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-greeting": { greetingMarginBottom: 16 },
      "concierge-home-logo": { marginBottom: 64 },
      "concierge-chat-input": EXPANDED_CHAT_INPUT,
    },
  },

  /** B5 - Pills + Branded Footer: Greeting with pills and "EMBER BY" footer */
  B5: {
    layout: {
      logoPosition: "footer",
      brandLabelPosition: "above-footer-logo",
      showGreeting: true,
      showGreetingIcon: false,
      subtitlePosition: "under-greeting",
      startersMode: "pills",
      maxStarters: 4,
      cardLayoutDirection: "stacked",
      footerGap: 8,
      footerPaddingBottom: 0,
      searchInputGap: 32,
      mobile_logoPosition: "header",
      mobile_startersMode: "inherit",
    },
    childWidgets: {
      ...SHARED_CHILD_WIDGETS,
      "concierge-home-greeting": { greetingMarginBottom: 16 },
      "concierge-home-logo": { marginBottom: 64 },
      "concierge-chat-input": EXPANDED_CHAT_INPUT,
    },
  },
};

/**
 * Human-readable labels for preset dropdown in the edit panel.
 */
export const PRESET_LABELS: Record<LayoutPreset, string> = {
  A1: "A1 - Minimal (Logo only)",
  A2: "A2 - Logo + Subtitle",
  A3: "A3 - Branded Logo",
  A4: "A4 - Subtitle + Pills",
  A5: "A5 - Greeting + Pills",
  A6: "A6 - Inline Icon Greeting + Pills",
  A7: "A7 - Icon Above Greeting + Pills",
  B1: "B1 - Card Starters (Row)",
  B2: "B2 - Icon Pills",
  B3: "B3 - Icon Pills + Branded Footer",
  B4: "B4 - Card Starters (Grid)",
  B5: "B5 - Pills + Branded Footer",
  custom: "Custom",
};
